import type { Territory } from "$global-providers/config-provider/provider";
import { Box, makeStyles, Typography } from "@material-ui/core";
import { defineMessages, FormattedMessage, useIntl } from "react-intl";

import { DashboardLink } from "./DashboardLink";
import {
  type DASHBOARD_TYPE,
  generateDashboardURL,
} from "./generateDashboardUrl";

const useStyles = makeStyles((theme) => ({
  title: {
    color: theme.palette.grey[600],
    textTransform: "uppercase",
  },
}));

const translations = defineMessages({
  FACILITY: {
    id: "sidebar.dashboard.facility",
    defaultMessage: "Facility Dashboard",
  },
  LABORATORY: {
    id: "sidebar.dashboard.laboratory",
    defaultMessage: "Laboratory Dashboard",
  },
  BILLING: {
    id: "sidebar.dashboard.billing",
    defaultMessage: "Billing Dashboard",
  },
  FACILITY_INVENTORY: {
    id: "sidebar.dashboard.facilityInventory",
    defaultMessage: "Inventory Dashboard",
  },
  ORGANISATION_INVENTORY: {
    id: "sidebar.dashboard.organisationInventory",
    defaultMessage: "Organisation Inventory Dashboard",
  },
  MONTHLY_SUMMARY: {
    id: "sidebar.dashboard.monthlySummary",
    defaultMessage: "Monthly Summary Dashboard",
  },
});

interface Props {
  roleName: string;
  territory: Territory;
  organisationId: string;
  facilityId: string;
}

export const DashboardLinks: FC<Props> = ({
  roleName,
  territory,
  facilityId,
  organisationId,
}) => {
  const { formatMessage } = useIntl();
  const classes = useStyles();

  const makeLinkProps = (type: DASHBOARD_TYPE) => ({
    label: formatMessage(translations[type]),
    dashboardURL: generateDashboardURL({
      territory,
      facilityId,
      organisationId,
      type,
      roleName,
    }),
  });

  const facilityProps = makeLinkProps("FACILITY");
  const laboratoryProps = makeLinkProps("LABORATORY");
  const billingProps = makeLinkProps("BILLING");
  const facilityInventoryProps = makeLinkProps("FACILITY_INVENTORY");
  const organisationInventoryProps = makeLinkProps("ORGANISATION_INVENTORY");
  const monthlySummaryProps = makeLinkProps("MONTHLY_SUMMARY");
  const showFacilityDashboard = facilityProps.dashboardURL;
  const showLaboratoryDashboard = laboratoryProps.dashboardURL;
  const showBillingDashboard = billingProps.dashboardURL;
  const showFacilityInventoryDashboard = facilityInventoryProps.dashboardURL;
  const showOrganisationInventoryDashboard =
    organisationInventoryProps.dashboardURL;
  const showMonthlySummaryDashboard = monthlySummaryProps.dashboardURL;

  // If we don't get any urls back don't show anything
  if (
    !showFacilityDashboard &&
    !showLaboratoryDashboard &&
    !showBillingDashboard &&
    !showFacilityInventoryDashboard &&
    !showOrganisationInventoryDashboard &&
    !showMonthlySummaryDashboard
  )
    return null;

  return (
    <>
      <Box px={2} pt={2} pb={1}>
        <Typography variant="body2" className={classes.title}>
          <FormattedMessage
            id="sidebar.dashboard.title"
            defaultMessage="Dashboards"
          />
        </Typography>
      </Box>
      {showFacilityDashboard && <DashboardLink {...facilityProps} />}
      {showLaboratoryDashboard && <DashboardLink {...laboratoryProps} />}
      {showBillingDashboard && <DashboardLink {...billingProps} />}
      {showFacilityInventoryDashboard && (
        <DashboardLink {...facilityInventoryProps} />
      )}
      {showOrganisationInventoryDashboard && (
        <DashboardLink {...organisationInventoryProps} />
      )}
      {showMonthlySummaryDashboard && (
        <DashboardLink {...monthlySummaryProps} />
      )}
    </>
  );
};
